import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Section } from '@vnetwork/ui/layout';
import { Badge, Explainer } from '@vnetwork/ui/primitives';
import { HlsPreviewPlayer } from '../_shared/HlsPreviewPlayer';
import { useSchedules, type Channel } from '@/api/fastchannel';
import {
  channelM3u8Url,
  liveProgram,
  scheduleTypeLabel,
  formatDurationClock,
  type LiveProgram,
} from './shared';
import styles from './Fastchannel.module.css';

/**
 * "Now playing" banner — the program on air at the current wall-clock instant,
 * computed with the same time-addressed function the schedule timeline uses, so
 * it matches exactly what the packager emits (including looped channels). null
 * → a gap / flushed schedule (the packager is showing the slate backdrop).
 */
function NowPlayingBanner({ live }: { live: LiveProgram | null }) {
  if (!live) {
    return (
      <div className={styles.previewBanner}>
        <span className={styles.previewBannerLabel}>On air</span>
        <span className={styles.previewBannerSlate}>Slate (no program scheduled)</span>
      </div>
    );
  }
  const { program, offsetMs } = live;
  const slotMs = Math.max(0, program.end_time - program.start_time);
  return (
    <div className={styles.previewBanner}>
      <div className={styles.previewBannerMain}>
        <span className={styles.previewBannerLabel}>Now playing</span>
        <span className={styles.previewBannerTitle} title={program.title}>
          {program.title || 'Untitled program'}
        </span>
      </div>
      <Badge tone={typeTone(program.type)}>{scheduleTypeLabel(program.type)}</Badge>
      <span className={styles.previewBannerTime}>
        {formatDurationClock(offsetMs / 1000)} / {formatDurationClock(slotMs / 1000)}
      </span>
    </div>
  );
}

/**
 * "How it works" explainer above the preview, so the linear-playout behaviour
 * (joins mid-program, runs slightly behind real time) reads as intended, not as
 * a bug. Uses the design-system Explainer + the shared muted-list body style.
 */
function PlayoutNote() {
  return (
    <Explainer
      title="How this channel plays"
      description="Linear playout joins mid-program and runs slightly behind real time"
    >
      <ul
        style={{
          display: 'grid',
          gap: 'var(--vn-space-3)',
          margin: 0,
          paddingLeft: 'var(--vn-space-4)',
          fontSize: 'var(--vn-font-size-sm)',
          color: 'var(--vn-fg-muted)',
          lineHeight: 'var(--vn-line-height-normal)',
        }}
      >
        <li>
          <strong>It’s a 24/7 linear channel.</strong> Programs air on a fixed wall-clock
          timeline and the lineup repeats — the player follows the live edge, it does not
          start a video from the beginning.
        </li>
        <li>
          <strong>Joining mid-program is normal.</strong> If a program’s scheduled slot is
          already underway, playback starts partway in — like tuning into a TV channel
          mid-show — not at 0:00.
        </li>
        <li>
          <strong>“Now playing” matches the picture.</strong> The HLS stream runs ~10–15s
          behind real time (normal live buffering); the banner is aligned to what the player
          is actually showing, not to wall-clock now.
        </li>
        <li>
          <strong>Schedule edits apply at the next program boundary</strong> (within a short
          refresh); a re-sync applies them immediately.
        </li>
        <li>
          To review a single asset <strong>from the start</strong>, open its source playlist
          directly from the Content Library rather than the channel.
        </li>
      </ul>
    </Explainer>
  );
}

/** Map a schedule type to a Badge tone. */
function typeTone(type: string | undefined): 'danger' | 'info' | 'warning' | 'neutral' {
  switch (type) {
    case 'live':
      return 'danger';
    case 'ads':
      return 'info';
    case 'vod':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function PreviewTab({ channel }: { channel: Channel }) {
  const { serviceUuid } = useParams<{ serviceUuid: string }>();
  const src = channel.id ? channelM3u8Url(channel.id) : '';

  // Resolve the program on air now, the same way the timeline + packager do.
  // Tick once a second so the banner rolls over at program boundaries.
  const { data: schedules = [] } = useSchedules(serviceUuid, channel.id);
  const [now, setNow] = useState(() => Date.now());
  // Seconds the player trails the live edge (hls.js live latency). The banner
  // must reflect what the viewer is SEEING, not wall-clock now — otherwise it
  // flips to the next program ~one live-latency early (≈10–12s).
  const [latencyMs, setLatencyMs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const onLatency = useCallback((s: number) => setLatencyMs(Math.round(s * 1000)), []);
  const live = useMemo(
    () => liveProgram(schedules, channel.repeat ?? true, now - latencyMs),
    [schedules, channel.repeat, now, latencyMs],
  );

  return (
    <Section title="Preview" description="Live preview of the channel's HLS output.">
      <div style={{ marginBottom: 'var(--vn-space-4)' }}>
        <PlayoutNote />
      </div>
      <HlsPreviewPlayer
        src={src}
        live
        title={channel.title || channel.name}
        description={`${channel.tenant ? `${channel.tenant} · ` : ''}${channel.description || 'Adaptive HLS stream'}`}
        banner={channel.id ? <NowPlayingBanner live={live} /> : undefined}
        emptyMessage="This channel has no stream yet."
        onLatency={onLatency}
      />
    </Section>
  );
}
